/**
 * BackdraftSkillEffect — Fire tree backdraft-path active skill.
 *
 * Detonate a smoke cloud within 2 tiles of the player. Damage scales with
 * cloud age: 10 at fresh (0ms elapsed), 25 at fully aged (≥ cloud duration).
 * Applies explosion damage to all enemies within the detonated zone's radius.
 * Fails silently (cooldown consumed) if no detonatable zone is in range.
 *
 * Overpressure (`backdraft_pressure`): adds knockback and stagger to the
 * blast — resolver.getAdditiveEffect('backdraft', 'knockbackTiles') and
 * 'staggerDurationMs'.
 *
 * Secondary Burn (`backdraft_secondary`): spawns a fresh 2-tile smoke cloud
 * at the detonation site after each detonation — resolver.hasEffect('backdraft',
 * 'detonationSmokeTiles').
 *
 * FlashpointSkillEffect extends this class and overrides _detonationTypes to
 * include 'fire_zone' in addition to 'smoke_cloud'.
 *
 * @see fire-spec.md § Backdraft Path (Fork 1-B)
 * @see ADR-064 (shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Zone info returned by findNearestDetonatableZone
// ---------------------------------------------------------------------------

/**
 * Snapshot of an active zone returned by findNearestDetonatableZone.
 * Contains everything BackdraftSkillEffect needs to compute damage and
 * apply post-detonation effects.
 */
export interface BackdraftZoneInfo {
  readonly id: string;
  readonly type: string;
  readonly position: { readonly x: number; readonly y: number };
  /** Time already elapsed since zone creation (ms). elapsedMs = durationMs − remainingMs */
  readonly elapsedMs: number;
  /** Total configured duration of the zone (ms). Used for age ratio. */
  readonly durationMs: number;
  /** Radius in tiles — used as the explosion blast radius. */
  readonly radiusTiles: number;
}

// ---------------------------------------------------------------------------
// Zone create config (subset of ZoneConfig)
// ---------------------------------------------------------------------------

export interface BackdraftZoneCreateConfig {
  id: string;
  type: string;
  position: { x: number; y: number };
  shape: 'circle';
  radiusTiles: number;
  durationMs: number;
  detectionReduction: number;
  sourceSkillId: string;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface BackdraftDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
    /**
     * Query a tree-wide passive accumulator. Smothered (choke_passive) registers
     * `smokeCloudDurationBonus` under treeId `'fire'` with `'sum'` aggregation.
     * Any code that creates a smoke_cloud zone must add this value to durationMs.
     */
    getTreePassiveEffect(treeId: string, key: string, aggregation: 'sum' | 'product'): number;
  };

  /**
   * Find the nearest zone whose type is one of `types`, within `maxTiles`
   * of the given world position. Returns null if none qualify.
   *
   * In game code this delegates to ZoneSystem.getAll() + distance comparison.
   * In tests, returning a pre-built BackdraftZoneInfo controls detonation.
   */
  readonly findNearestDetonatableZone: (
    x: number,
    y: number,
    types: readonly string[],
    maxTiles: number,
  ) => BackdraftZoneInfo | null;

  readonly zones: {
    remove(id: string): void;
    create(config: BackdraftZoneCreateConfig): string;
  };

  /** Return entity IDs of all enemies within `radiusTiles` of (x, y). */
  readonly getEnemiesInRadius: (x: number, y: number, radiusTiles: number) => string[];

  /** Deal direct damage to a target. */
  readonly dealDamage: (entityId: string, damage: number) => void;

  /**
   * Push `entityId` away from the explosion centre (fromX, fromY) by `tiles`.
   * Direction is computed from the explosion centre outward, not from the player.
   */
  readonly applyKnockback: (fromX: number, fromY: number, entityId: string, tiles: number) => void;

  /** Apply a status effect (e.g. 'stagger') to an enemy entity. */
  readonly applyEnemyStatus: (
    entityId: string,
    statusId: string,
    opts: { durationMs: number },
  ) => void;

  /** True if the entity is currently within a CC immunity window for statusId. */
  readonly isImmune: (entityId: string, statusId: string) => boolean;

  /** Return the player's current world position. */
  readonly getPlayerPosition: () => { x: number; y: number };

  /** Break stealth / hide state on activation. */
  readonly breakHide: () => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface BackdraftConfig {
  /** Cooldown in ms after any activation (including silent-fail). */
  readonly cooldownMs: number;
  /** Maximum interaction range in tiles (detonatable zones beyond this are ignored). */
  readonly interactionRangeTiles: number;
  /** Damage at age ratio 0.0 (fresh cloud just spawned). */
  readonly damageLow: number;
  /** Damage at age ratio 1.0 (cloud at or past its configured duration). */
  readonly damageHigh: number;
  /**
   * `detectionReduction` written into the post-detonation smoke cloud created
   * by Secondary Burn. Matches the Smoke passive's cloud value (0.3).
   */
  readonly postDetonationSmokeDetectionReduction: number;
}

const DEFAULT_CONFIG: BackdraftConfig = {
  cooldownMs: 1000,                         // "medium" cooldown tier
  interactionRangeTiles: 2,
  damageLow: 10,
  damageHigh: 25,
  postDetonationSmokeDetectionReduction: 0.3,
};

// ---------------------------------------------------------------------------
// Resolver keys
// ---------------------------------------------------------------------------

const KEY_KNOCKBACK_TILES = 'knockbackTiles';
const KEY_STAGGER_DURATION_MS = 'staggerDurationMs';
const KEY_DETONATION_SMOKE_TILES = 'detonationSmokeTiles';
const KEY_DETONATION_SMOKE_DURATION_MS = 'detonationSmokeDurationMs';

// ---------------------------------------------------------------------------
// Zone types this skill can detonate (Flashpoint overrides to add fire_zone)
// ---------------------------------------------------------------------------

const BACKDRAFT_ZONE_TYPES: readonly string[] = ['smoke_cloud'];

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class BackdraftSkillEffect implements SkillEffect {
  readonly skillId = 'backdraft';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  /** Zone types this instance can detonate. Overridable for Flashpoint. */
  protected readonly _detonationTypes: readonly string[];

  private _cooldownRemaining = 0;

  // Counters exposed for scenario assertions
  private _activations = 0;
  private _silentFails = 0;

  constructor(
    protected readonly _deps: BackdraftDeps,
    protected readonly _config: BackdraftConfig = DEFAULT_CONFIG,
    detonationTypes: readonly string[] = BACKDRAFT_ZONE_TYPES,
  ) {
    this._detonationTypes = detonationTypes;
  }

  // --- Accessors ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get silentFails(): number { return this._silentFails; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    const pos = this._deps.getPlayerPosition();

    // Find nearest detonatable zone within interaction range
    const zone = this._deps.findNearestDetonatableZone(
      pos.x,
      pos.y,
      this._detonationTypes,
      this._config.interactionRangeTiles,
    );

    // Cooldown always consumed — even on no-cloud silent fail
    this._cooldownRemaining = this._config.cooldownMs;

    if (!zone) {
      // Silent fail: no zone in range
      this._silentFails++;
      return false;
    }

    this._deps.breakHide();

    // Age ratio: 0 = fresh, 1 = fully aged (clamped)
    const ageRatio = Math.min(zone.elapsedMs / zone.durationMs, 1.0);
    const damage = this._config.damageLow
      + (this._config.damageHigh - this._config.damageLow) * ageRatio;

    // Blast: damage all enemies in zone's radius
    const enemies = this._deps.getEnemiesInRadius(
      zone.position.x,
      zone.position.y,
      zone.radiusTiles,
    );

    for (const enemyId of enemies) {
      this._deps.dealDamage(enemyId, damage);
    }

    // Overpressure: knockback away from explosion centre
    const knockbackBonus = this._deps.resolver.getAdditiveEffect(
      this.skillId,
      KEY_KNOCKBACK_TILES,
    );
    if (knockbackBonus > 0) {
      for (const enemyId of enemies) {
        this._deps.applyKnockback(
          zone.position.x,
          zone.position.y,
          enemyId,
          knockbackBonus,
        );
      }
    }

    // Overpressure: stagger enemies (respects CC immunity window)
    const staggerMs = this._deps.resolver.getAdditiveEffect(
      this.skillId,
      KEY_STAGGER_DURATION_MS,
    );
    if (staggerMs > 0) {
      for (const enemyId of enemies) {
        if (!this._deps.isImmune(enemyId, 'stagger')) {
          this._deps.applyEnemyStatus(enemyId, 'stagger', { durationMs: staggerMs });
        }
      }
    }

    // Remove the detonated zone
    this._deps.zones.remove(zone.id);

    // Secondary Burn: spawn a fresh smoke cloud at the detonation site
    if (this._deps.resolver.hasEffect(this.skillId, KEY_DETONATION_SMOKE_TILES)) {
      const smokeRadius = this._deps.resolver.getAdditiveEffect(
        this.skillId,
        KEY_DETONATION_SMOKE_TILES,
      );
      const baseDurationMs = this._deps.resolver.getAdditiveEffect(
        this.skillId,
        KEY_DETONATION_SMOKE_DURATION_MS,
      );
      // Apply Smothered (choke_passive) bonus — adds durationMs to all smoke_cloud zones
      const smotheredBonusMs = this._deps.resolver.getTreePassiveEffect(
        'fire',
        'smokeCloudDurationBonus',
        'sum',
      );
      this._deps.zones.create({
        id: `backdraft_secondary_smoke_${Date.now()}`,
        type: 'smoke_cloud',
        position: { x: zone.position.x, y: zone.position.y },
        shape: 'circle',
        radiusTiles: smokeRadius,
        durationMs: baseDurationMs + smotheredBonusMs,
        detectionReduction: this._config.postDetonationSmokeDetectionReduction,
        sourceSkillId: 'backdraft_secondary',
      });
    }

    this._activations++;
    return true;
  }

  deactivate(): void {}

  isActive(): boolean { return false; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
